import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FaGraduationCap, 
  FaBookOpen, 
  FaLock, 
  FaUnlock, 
  FaCheckCircle, 
  FaClock, 
  FaFileAlt, 
  FaQuestionCircle, 
  FaChevronDown, 
  FaChevronUp,
  FaMicrophone,
  FaPenNib
} from 'react-icons/fa';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

const VeptHub = () => {
    const navigate = useNavigate();
    const [isPremium, setIsPremium] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    // 20 Full Length VEPT Mock Tests
    const tests = Array.from({ length: 20 }, (_, i) => ({
        id: i + 1,
        title: `VEPT Full Mock Exam ${i + 1}`,
        description: `Complete 50-minute test covering 9 task types across Speaking, Listening, Reading & Writing.`,
        duration: '50 mins',
        questions: 81,
        difficulty: i < 5 ? 'Easy' : i < 15 ? 'Medium' : 'Hard'
    }));

    useEffect(() => {
        const checkAccess = async () => {
            const auth = getAuth();
            const user = auth.currentUser;
            if (user) {
                const docRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists() && (docSnap.data().paidTests || docSnap.data().hasPaid)) {
                    setIsPremium(true);
                } else {
                    setIsPremium(false);
                }
            } else {
                setIsPremium(false);
            }
        };
        checkAccess();
    }, []);

    const handleStartTest = (testId) => {
        if (!isPremium && testId > 1) {
            navigate('/pricing');
            return;
        }
        navigate(`/versant-vept-mock-test/start/${testId}`, { state: { testId } });
    };

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const faqs = [
        {
            q: "What is the VEPT (Versant English Placement Test)?",
            a: "The Versant English Placement Test (VEPT) is a 50-minute computer-administered exam. It evaluates complete English proficiency—including Speaking, Listening, Reading, and Writing—to place candidates in academic programs or corporate job roles."
        },
        {
            q: "How many questions are on the VEPT exam?",
            a: "The complete VEPT consists of 9 distinct task sections containing approximately 81 total test questions covering Read Aloud, Repeat Sentences, Sentence Builds, Conversations, Typing, Sentence Completion, Dictation, Passage Reconstruction, and Summary & Opinion."
        },
        {
            q: "How is the VEPT score calculated?",
            a: "VEPT uses automated AI speech and language scoring technology to grade spoken fluency, pronunciation, grammar, vocabulary, reading comprehension, and written coherence on a scale from 20 to 80, mapped to the Global Scale of English (GSE) and CEFR levels (A1 to C2)."
        },
        {
            q: "What score is required to pass the VEPT for university admissions?",
            a: "Most international universities, colleges, and academic language pathway programs require an overall VEPT score of 53 to 65+ (CEFR Level B2 or C1) for course entrance and exemption from remedial English courses."
        }
    ];

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900 font-sans selection:bg-emerald-200">
            <Helmet>
                <title>VEPT Practice Test 2026 | Free Versant English Placement Test Mock Exams</title>
                <meta name="description" content="Prepare for the Versant English Placement Test (VEPT) with 20 free full-length mock exams. Instant AI score reports for Speaking, Listening, Reading, and Writing." />
                <link rel="canonical" href="https://versantpro.com/versant-vept-mock-test" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "WebPage",
                                "@id": "https://versantpro.com/versant-vept-mock-test/#webpage",
                                "url": "https://versantpro.com/versant-vept-mock-test",
                                "name": "VEPT Practice Test 2026",
                                "description": "Prepare for the Versant English Placement Test (VEPT) with 20 free full-length mock exams."
                            },
                            {
                                "@type": "EducationalApplication",
                                "@id": "https://versantpro.com/versant-vept-mock-test/#application",
                                "name": "VEPT AI Practice Simulator",
                                "operatingSystem": "All",
                                "applicationCategory": "EducationalApplication",
                                "offers": {
                                    "@type": "Offer",
                                    "price": "0.00",
                                    "priceCurrency": "USD"
                                }
                            },
                            {
                                "@type": "FAQPage",
                                "@id": "https://versantpro.com/versant-vept-mock-test/#faq",
                                "mainEntity": faqs.map(item => ({
                                    "@type": "Question",
                                    "name": item.q,
                                    "acceptedAnswer": {
                                        "@type": "Answer",
                                        "text": item.a
                                    }
                                }))
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Header page="veptHub" />

            {/* Hero Banner Section */}
            <section className="relative w-full pt-16 pb-24 px-6 overflow-hidden bg-gradient-to-br from-emerald-900 via-green-800 to-teal-900 text-white">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay pointer-events-none"></div>
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
                
                <div className="max-w-6xl mx-auto relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-emerald-100 text-xs sm:text-sm font-semibold mb-6 border border-white/20 shadow-lg">
                        <span className="w-2.5 h-2.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.8)]"></span>
                        Updated for 2026 VEPT 50-Minute Standard Test
                    </div>
                    
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight mb-6 leading-tight drop-shadow-md">
                        Versant English Placement Test (VEPT) <br className="hidden sm:block"/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-amber-300">
                            20 Free Full-Length AI Mock Exams
                        </span>
                    </h1>
                    
                    <p className="text-lg sm:text-xl text-emerald-100 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
                        Comprehensive 4-skills assessment simulator. Evaluate Speaking, Listening, Reading, and Writing in a single timed exam with instant GSE & CEFR score reports.
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8 text-xs sm:text-sm font-semibold text-emerald-200">
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> 20 Full 50-Min VEPT Mocks</span>
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> 4-Skills Integrated Evaluation</span>
                        <span className="flex items-center gap-1.5"><FaCheckCircle className="text-emerald-400" /> Complete 20-80 Scale Score breakdown</span>
                    </div>
                </div>
            </section>

            {/* Educational SEO Guide Section */}
            <section className="max-w-6xl mx-auto px-6 py-16 -mt-10 relative z-20">
                <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xl p-6 sm:p-10 space-y-8">
                    
                    <div className="border-b border-slate-100 pb-6">
                        <div className="inline-flex items-center gap-2 text-emerald-600 font-extrabold text-xs uppercase tracking-widest bg-emerald-50 px-3 py-1 rounded-full mb-3">
                            <FaGraduationCap /> Complete Exam Guide
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                            Understanding the VEPT Exam Architecture
                        </h2>
                        <p className="text-slate-600 text-sm sm:text-base leading-relaxed mt-3 font-normal">
                            The Versant English Placement Test (VEPT) is an official 4-skills assessment designed to evaluate non-native English speakers across spoken and written communication. Recognized globally by international universities, colleges, and higher-education institutions, VEPT provides an objective CEFR scorecard used for academic admissions, course exemption, and English language program placement.
                        </p>
                    </div>

                    {/* 4 Core Pillars Breakdown */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-2.5 font-bold text-slate-900 text-base mb-2">
                                <FaMicrophone className="text-emerald-600" />
                                <span>1. Speaking</span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed font-light">
                                Evaluates sentence rhythm, acoustic clarity, vowel stress, and natural speech delivery during Read Aloud & Repeats.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-2.5 font-bold text-slate-900 text-base mb-2">
                                <FaBookOpen className="text-emerald-600" />
                                <span>2. Listening</span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed font-light">
                                Measures real-time comprehension of fast native audio conversations and dictation tasks.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-2.5 font-bold text-slate-900 text-base mb-2">
                                <FaPenNib className="text-emerald-600" />
                                <span>3. Reading</span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed font-light">
                                Tests vocabulary context, syntax recognition, and speed comprehension in sentence completion.
                            </p>
                        </div>

                        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 hover:border-emerald-200 transition-colors">
                            <div className="flex items-center gap-2.5 font-bold text-slate-900 text-base mb-2">
                                <FaFileAlt className="text-emerald-600" />
                                <span>4. Writing</span>
                            </div>
                            <p className="text-xs text-slate-600 leading-relaxed font-light">
                                Assesses grammatical precision, paragraph construction, and typing speed in Passage Reconstruction & Opinion essays.
                            </p>
                        </div>
                    </div>

                    {/* Action Card Banner */}
                    <div className="bg-gradient-to-r from-slate-900 to-emerald-950 text-white rounded-2xl p-6 border border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="space-y-2">
                            <span className="inline-block px-3 py-1 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded-full border border-emerald-500/30 uppercase tracking-wider">
                                🎯 Complete 50-Min Test
                            </span>
                            <h3 className="text-xl font-bold">Simulate Real VEPT Exam Conditions</h3>
                            <p className="text-xs sm:text-sm text-slate-300 max-w-2xl font-light leading-relaxed">
                                Practice under real time limits with exact audio recordings and instant AI scoring on all 9 sections.
                            </p>
                        </div>
                        <a 
                          href="#vept-tests-grid" 
                          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg transition-transform active:scale-95 whitespace-nowrap shrink-0"
                        >
                            Try Free Demo Test ➔
                        </a>
                    </div>

                </div>
            </section>

            {/* 20 Mock Tests Grid Section */}
            <main id="vept-tests-grid" className="max-w-6xl mx-auto px-6 pb-20">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
                            <span className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-lg">
                                🎓
                            </span>
                            VEPT Full Mock Exams (1-20)
                        </h2>
                        <p className="text-slate-500 text-sm mt-1">Select a full-length 50-minute exam below to begin testing.</p>
                    </div>
                    
                    {!isPremium && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-2 self-start sm:self-auto">
                            <span>⭐ Test 1 is 100% Free. Upgrade to unlock Tests 2–20.</span>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {tests.map((test) => {
                        const isLocked = !isPremium && test.id > 1;

                        return (
                            <div
                                key={test.id}
                                className={`relative group bg-white rounded-3xl p-6 border-2 transition-all duration-300 flex flex-col justify-between ${
                                    isLocked
                                      ? 'border-slate-200/80 bg-slate-50/50 opacity-80 hover:opacity-100 hover:border-slate-300'
                                      : 'border-emerald-100 hover:border-emerald-500 shadow-md hover:shadow-2xl hover:-translate-y-1'
                                }`}
                            >
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className={`p-3 rounded-2xl ${isLocked ? 'bg-slate-200 text-slate-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                            <FaGraduationCap className="text-xl" />
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            {test.id === 1 && !isPremium && (
                                                <span className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-black uppercase px-2.5 py-1 rounded-full shadow-md animate-pulse">
                                                    FREE DEMO
                                                </span>
                                            )}
                                            
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                                test.difficulty === 'Easy' ? 'bg-green-100 text-green-700' :
                                                test.difficulty === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                                'bg-rose-100 text-rose-700'
                                            }`}>
                                                {test.difficulty}
                                            </span>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-extrabold text-slate-900 mb-2 group-hover:text-emerald-600 transition-colors">
                                        {test.title}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mb-6 font-light">
                                        {test.description}
                                    </p>
                                </div>

                                <div>
                                    <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 mb-5 border-t border-slate-100 pt-4">
                                        <span className="flex items-center gap-1.5"><FaClock className="text-slate-400" /> {test.duration}</span>
                                        <span className="flex items-center gap-1.5"><FaFileAlt className="text-slate-400" /> {test.questions} Qs</span>
                                    </div>

                                    <button
                                        onClick={() => handleStartTest(test.id)}
                                        className={`w-full py-3.5 rounded-2xl font-extrabold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 ${
                                            isLocked
                                              ? 'bg-slate-200 text-slate-600 hover:bg-emerald-600 hover:text-white'
                                              : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 shadow-emerald-500/20'
                                        }`}
                                    >
                                        {isLocked ? (
                                            <>Unlock VEPT Test {test.id} <FaLock className="text-xs" /></>
                                        ) : (
                                            <>Start VEPT Exam <FaUnlock className="text-xs" /></>
                                        )}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </main>

            {/* FAQ Accordion Section */}
            <section className="bg-white border-t border-slate-200/80 py-16 px-6">
                <div className="max-w-4xl mx-auto space-y-8">
                    <div className="text-center space-y-3">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full uppercase tracking-wider">
                            <FaQuestionCircle /> VEPT FAQ
                        </span>
                        <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                            Frequently Asked Questions
                        </h2>
                        <p className="text-slate-500 text-sm sm:text-base font-light">
                            Everything you need to know about the Versant English Placement Test format and scoring.
                        </p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div 
                              key={index} 
                              className="border border-slate-200 rounded-2xl overflow-hidden transition-all duration-200 hover:border-emerald-300"
                            >
                                <button
                                  onClick={() => toggleFaq(index)}
                                  className="w-full p-5 sm:p-6 text-left font-bold text-slate-900 text-base sm:text-lg flex justify-between items-center gap-4 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                                >
                                    <span>{faq.q}</span>
                                    <span className="text-emerald-600 shrink-0 text-sm">
                                        {openFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                                    </span>
                                </button>
                                {openFaq === index && (
                                    <div className="p-5 sm:p-6 bg-white text-slate-600 text-sm sm:text-base leading-relaxed font-light border-t border-slate-100">
                                        {faq.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default VeptHub;
